import { useTranslation } from "next-i18next";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import {
  ICartUtilQtyUpdate,
  IHandleDecrementalQty,
  IHandleIncrementalQty,
} from "@Types/cartUtils";
import { getItem, setItem } from "@Utils/browserStorage";
import {
  cartUtilQtyAdd,
  cartUtilQtyOnBlurHandler,
  cartUtilQtyRemove,
} from "@Utils/cartUtils";
import {
  sendTrackKlaviyoEvent,
  sendViewedItemKlaviyoEvent,
  getFormattedAddToCartDataForKlaviyo,
} from "@Utils/klaviyo";
import { isCanadianLocale } from "@Utils/locale";
import { getFullPath } from "@Utils/url";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { useAppSelector } from "@wff/store/hooks";
import { toggleZipCodePopUp, ZipCodeSelector } from "@wff/store/zipcodeSlice";
import { Button, TextField } from "components/ui";
import classes from "./ButtonCartAddOrUpdate.module.scss";

interface IButtonCartAddOrUpdate {
  decrementalPayload: IHandleDecrementalQty;
  handleOnChangeQuantity: Function;
  handleUpdateQuantityPayload: ICartUtilQtyUpdate;
  incrementalPayload: IHandleIncrementalQty;
  isOnStock: boolean;
  itemQuantity: number | null;
  qtyInputValue: number | null;
  currentProduct?: any | null;
  productPrice?: any | null;
  isDisplayTwoInRow?: boolean;
  showTwoInRowCarousel?: boolean;
  productDetailPage?: boolean;
}

export default function ButtonCartAddOrUpdate({
  decrementalPayload,
  handleOnChangeQuantity,
  handleUpdateQuantityPayload,
  incrementalPayload,
  isOnStock,
  itemQuantity,
  qtyInputValue,
  currentProduct,
  productPrice,
  isDisplayTwoInRow,
  showTwoInRowCarousel,
  productDetailPage,
}: IButtonCartAddOrUpdate) {
  const { asPath } = useRouter();
  const cartData = useAppSelector(cartSliceSelector);
  const { availability, isPickupAvailable } = useAppSelector(ZipCodeSelector);
  const { t } = useTranslation("common");
  const { languageCode } = useAppSelector(({ locale }) => locale);
  const isCanadianUser = isCanadianLocale(languageCode);
  const dispatch = useDispatch();
  const [addToCartButtonStatus, setAddToCartButtonStatus] = useState(false);
  const { cartId } = cartData;

  const GTMVaddToCart = (items: any, productPrice: any) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "AddToCart",
      AddToCart: {
        content_type: "Product",
        contents: items, // TODO from Jared: investigate this - should it be the entire cartState or lineItems? Change the variable name and type accordingly
        currency: "CAD",
        value: productPrice,
      },
    });
  };

  const dyQtyChangeEventPrevValue = getItem(
    STORAGE_KEYS.DY_QTY_CHANGE_EVENT_PREV_VALUE,
    STORAGE_TYPES.SESSION
  );

  /**
   * Add Item quantity change value to handleUpdateQuantityPayload object.
   * Later using for DY `Quantity Change` event
   */
  handleUpdateQuantityPayload = {
    ...handleUpdateQuantityPayload,
    dyQtyChangeEventPrevValue,
  };

  const sendToKlaviyoAddToCartProductEvent = () => {
    const url = getFullPath(asPath);

    const formattedProduct = getFormattedAddToCartDataForKlaviyo(
      currentProduct,
      cartData,
      languageCode,
      url
    );

    sendViewedItemKlaviyoEvent(formattedProduct);
    sendTrackKlaviyoEvent(["Added to Cart", formattedProduct]);
  };

  const currentZipCodeAvailabilityStoreIdInStorageForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );

  const currentZipCodePickupAvailabilityForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_PICKUP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );

  const isDeliveryAvailable =
    availability || currentZipCodeAvailabilityStoreIdInStorageForCA === "true";
  const pickupAvailable =
    isPickupAvailable || currentZipCodePickupAvailabilityForCA === "true";

  const getButtonClass = () => {
    let btnClass = "";
    if (productDetailPage) {
      btnClass = classes["product-detail-page__atc"] + " ";
    }
    if (!isOnStock) {
      btnClass += classes["quick-add__atc--secondary"];
    } else if (
      availability ||
      isPickupAvailable ||
      (isCanadianUser &&
        (!currentZipCodeAvailabilityStoreIdInStorageForCA ||
          !currentZipCodePickupAvailabilityForCA))
    ) {
      btnClass += classes["quick-add__atc--orange"];
    } else {
      btnClass += classes["quick-add__atc--tertiary"];
    }
    return btnClass;
  };

  const getButtonLabel = () => {
    if (!isOnStock) {
      return t("cart.buttons.find_similar");
    } else if (
      availability ||
      isPickupAvailable ||
      (isCanadianUser &&
        (!currentZipCodeAvailabilityStoreIdInStorageForCA ||
          !currentZipCodePickupAvailabilityForCA))
    ) {
      return t("cart.buttons.add_to_cart");
    } else {
      return t("cart.buttons.change_to_shop");
    }
  };

  const handleCartUtilQtyAdd = () => {
    if (!isCanadianUser && isOnStock) {
      cartUtilQtyAdd(cartId, incrementalPayload);
      sendToKlaviyoAddToCartProductEvent();
      GTMVaddToCart(currentProduct, productPrice);
    } else if (isOnStock) {
      //CA-ONLY LOGIC
      if (
        (currentZipCodeAvailabilityStoreIdInStorageForCA.length === 0 &&
          currentZipCodePickupAvailabilityForCA.length === 0) ||
        (!availability && !isPickupAvailable) ||
        (currentZipCodeAvailabilityStoreIdInStorageForCA === "false" &&
          currentZipCodePickupAvailabilityForCA === "false")
      ) {
        setItem(
          STORAGE_KEYS.INCREMENT_PAYLOAD_CA,
          JSON.stringify(incrementalPayload),
          STORAGE_TYPES.LOCAL
        );
        dispatch(toggleZipCodePopUp());
      } else {
        cartUtilQtyAdd(cartId, incrementalPayload);
        sendToKlaviyoAddToCartProductEvent();
        GTMVaddToCart(currentProduct, productPrice);
      }
    }
  };

  /**
   * For CA users, Add To Cart Button available for first login.
   * For US users, Add To Cart Button availability depends on the availability
   */

  //setClass here, logic: When there is no local storage information, button will show as ash color
  // catch the state whihc has no local storage information as avilability, and then apply classes
  useEffect(() => {
    isCanadianUser
      ? setAddToCartButtonStatus(false)
      : setAddToCartButtonStatus(true);
  }, [isCanadianUser]);

  const isDisplayCartItemCounter =
    itemQuantity && (isDeliveryAvailable || pickupAvailable);

  const quantitySelectorWrapperClasses = `${
    classes["quick-add--quantity-selector-wrapper"]
  } ${
    productDetailPage
      ? classes["product-detail-page__quantity-selector-wrapper"]
      : ""
  }`;

  return (
    <div
      className={`${classes["quick-add"]} ${
        isDisplayTwoInRow || showTwoInRowCarousel
          ? classes["quick-add_two-row"]
          : ""
      } ${productDetailPage ? classes["product-detail-page__wrapper"] : ""}`}
    >
      {!isOnStock ? (
        <div className={classes["button-filler"]} /> // requested to hide button in CTIM-2361
      ) : isDisplayCartItemCounter ? (
        <div className={quantitySelectorWrapperClasses}>
          <div className={`${classes["quantity-selector"]}`}>
            <Button
              data-testid="decrease-qty-button"
              className={`${classes["quantity-selector__button"]}`}
              onClick={() => cartUtilQtyRemove(decrementalPayload)}
            >
              {t("cart.buttons.button_decrement_label")}
            </Button>
            <TextField
              data-testid="test-input"
              className={`${classes["price-qty-value"]} ${
                classes[`price-qty-value-cart`]
              }`}
              value={qtyInputValue}
              onBlur={() =>
                cartUtilQtyOnBlurHandler(handleUpdateQuantityPayload)
              }
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleOnChangeQuantity(e.target.value)
              }
              onClick={() =>
                setItem(
                  STORAGE_KEYS.DY_QTY_CHANGE_EVENT_PREV_VALUE,
                  qtyInputValue?.toString() || "",
                  STORAGE_TYPES.SESSION
                )
              }
            />

            <Button
              data-testid="add-qty-button"
              onClick={() => cartUtilQtyAdd(cartId, incrementalPayload)}
              className={classes["quantity-selector__button"]}
            >
              {t("cart.buttons.button_increment_label")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          // TODO: Add onClick function to "Find similar products" or "Add to cart"
          className={getButtonClass()}
          data-testid="button-incremental"
          //For CA users, Add to Cart Button always enable
          disabled={addToCartButtonStatus ? !availability : false}
          onClick={handleCartUtilQtyAdd}
        >
          {getButtonLabel()}
        </Button>
      )}
    </div>
  );
}
